import Vue from 'vue'
import VueRouter, { RouteConfig } from 'vue-router'
import NotFound from '@/NotFound.vue'
import store from '@/store'
import LoginService from '@/LoginService';
import { UserMessageCommand } from '@/UserMessageCommand';
import { CheckSubscriptionBySessionIdCommand } from '@/CheckSubscriptionBySessionIdCommand';
import ConfirmAccountCommand from './ConfirmAccountCommand';
import { docPages } from './DocPages';

Vue.use(VueRouter)

const appRoutes: Array<RouteConfig> = [
    {
        path: '/',
        name: 'Home',
        component: () => import(/* webpackChunkName: "home" */ './Home.vue'),
        meta: { doLogin: true },
        async beforeEnter(to, from, next) {
            next()
        }
    },
    {
        path: '/setpassword',
        name: 'Set password',
        component: () => import(/* webpackChunkName: "resetpassword" */ './SetPassword.vue'),
        meta: { titleSuffix: ' - Set new password' }
    },
    {
        path: '/confirmaccount',
        meta: { allowAnonymous: true },
        beforeEnter: async function(to, from, next) {
            const code = to.query.code
            if(code) {
                await store.commit('pushCommand', new ConfirmAccountCommand(code as string))
            } else {
                await store.commit('pushCommand', new UserMessageCommand('Confirmation code invalid', 'error', false))
            }
            next('/')
        }
    },
    {
        path: '/setpasswordsuccess',
        meta: { allowAnonymous: true },
        beforeEnter: async function (to, from, next) {
            await store.commit('pushCommand', new UserMessageCommand('Password has been updated. You can now log in with your new password.', 'success', true))
            next('/')
        }
    },
    {
        path: '/subscribesuccess',
        beforeEnter: async function(to, from, next) {
            const sessionid = to.query.session_id
            if(sessionid) {
                await store.commit('pushCommand', new CheckSubscriptionBySessionIdCommand(sessionid as string))
            }
            next('/')
        }
    },
    {
        path: '/googlelogin',
        meta: {allowAnonymous: true},
        beforeEnter: async function(to, from, next) {
            if(to.query.error) {
                await store.commit('pushCommand', new UserMessageCommand(to.query.error as string, 'error', true));
                next('/')
            } else if(to.query?.code) {
                const state = to.query?.state as string|null;
                const googleLoginResponse = await LoginService.googleLogin(to.query.code as string);
                await store.commit('login', googleLoginResponse.data.user);
                next(googleLoginResponse.data.returnUrl ?? '/')

                store.commit
            }
        }
    },
    {
        path: '/:prefix(u|r)/:gpxUrl',
        component: () => import(/* webpackChunkName: "home" */ './Home.vue'),
        beforeEnter(to, from, next) {
            next()
        },
        meta: {doLogin: true}
    },
    {
        path: '*',
        beforeEnter: async function(to, from, next) {
            await store.commit('pushCommand', new UserMessageCommand('Path not found - showing home page instead.', 'error', false))
            next('/')
        }
    }
];

const docRoutes: Array<RouteConfig> = docPages.map(d => d.route)
const routes = appRoutes.concat(docRoutes)

const router = new VueRouter({
    mode: 'history',
    base: process.env.BASE_URL,
    routes
});

router.beforeEach(async (to, from, next) => {

    if(to.query.message === 'loginsuccess') {
        await store.commit('pushCommand', new UserMessageCommand('Logged in successfully', '', false))
    }

    if(to.query.message === 'notconfirmed') {
        await store.commit('pushCommand', new UserMessageCommand('You have not confirmed your account. Please check your email for a link, and re-register if necessary.', 'error', true))
    }

    if(to.query.message === 'notrecognized') {
        await store.commit('pushCommand', new UserMessageCommand('Username or password not recognized.', 'error', true))
    }

    if(to.query.clear === 'true') {
        localStorage.clear()
    }

    if(to.meta && to.meta.doLogin) {
        to.fullPath === to.path ? next() : next(to.path)
        if (!store.getters.isUserLoggedIn) {
            try {
                //try and login using loginToken cookie
                const loginresp = await LoginService.loginGet();
                if (loginresp.data !== null) {
                    await store.commit('login', loginresp.data);   
                }
            } catch (error) {
                console.warn(error);
            } finally {
                store.commit('loginAttempted')
                if(store.getters.claimsToBeSubscribed) {
                    LoginService.checkSubscription().then(result => {
                        if (result && result.data && !result.data.isValid) {
                            store.commit('setSubscriptionInvalid', result.data);
                        }
                    }, reject => {
                        console.warn('checkSubscription failed: ', reject)
                    })
                }
            }
        }
    } else {
        next() // (removing parameters breaks SetPassword) only remove ones that are consumed
    }

    
})

export default router
